// conflict-merge.ts — pure 3-way content resolver for merging a note with its sync "conflicted
// copy" from the Labyrinth Thread. Prefers the same merge3 engine the sync floor already trusts,
// with a lossless line-union as the safe fallback. No DOM / storage — plaintext in, merged out.

import { isStructurallySound, merge3 } from './merge3.js';
import { unionMerge } from './note-similarity.js';

/**
 * Merge `mine` (the surviving original) with `copy` (its conflicted-copy sidecar).
 *
 * When a durable common base is known, run the block-level 3-way `merge3` and trust its result
 * ONLY when it is conflict-free AND structurally sound (balanced fences / well-formed tables /
 * parseable whole-doc JSON) — exactly the bar the automatic sync merge holds itself to. Otherwise
 * (no base, overlapping edits, an unsound result, or a merge3 throw) fall back to a lossless line
 * union that keeps every unique line from both sides. `merge3` preserves the shared `# Title`
 * block; `unionMerge` strips the title line, so re-attach `mine`'s heading on the fallback path.
 */
export function mergeConflictContent(mergeBase: string | null, mine: string, copy: string): string {
  if (mergeBase != null) {
    try {
      const { merged, conflicts } = merge3(mergeBase, mine, copy);
      if (conflicts.length === 0 && isStructurallySound(merged)) return merged;
    } catch {
      // merge3 declined on this input — drop to the lossless union below.
    }
  }
  const nl = mine.indexOf('\n');
  const head = nl !== -1 && /^#{1,6}\s/.test(mine) ? `${mine.slice(0, nl)}\n\n` : '';
  return head + unionMerge(mine, copy);
}
